// Utilitaires partagés du Rédacteur (extraits de RedacteurPage.vue) :
// appel API authentifié + rendu HTML des documents (docx converti → HTML,
// avec ou sans zones de template). Utilisés par RedacteurPage,
// TemplateEditorModal et TemplateManagerModal.
import { getSupabaseToken } from '@/services/api';
import DOMPurify from 'dompurify';

const API_URL = process.env.VUE_APP_API_URL;

export async function apiFetch(path, opts = {}) {
  const token = await getSupabaseToken();
  const resp = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: `Bearer ${token}`,
      ...(opts.body && !(opts.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
    },
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${resp.status}`);
  }
  const data = await resp.json();
  // Routes longues (détection, enregistrement) : réponse 200 en streaming
  // heartbeat, les erreurs voyagent dans le corps ({"error": ...}).
  if (data && typeof data === 'object' && data.error) {
    throw new Error(data.error);
  }
  return data;
}

export function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// En-tête / pied de page Word (lecture seule, sanitisé)
export function chromeHtml(chrome, key) {
  const html = chrome && chrome[key];
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_URI_REGEXP: /^(?:https?:|data:image\/)/i,
  });
}

export function renderDocPlain(elements) {
  const renderItems = (items) => {
    let html = '';
    for (const el of items) {
      if (el.kind === 'table') {
        html += '<table class="doc-tbl">';
        for (const row of el.rows || []) {
          html += '<tr>';
          for (const cell of row) {
            html += `<td>${renderItems(cell.items || [])}</td>`;
          }
          html += '</tr>';
        }
        html += '</table>';
      } else {
        html += el.html;
      }
    }
    return html;
  };
  return DOMPurify.sanitize(renderItems(elements), {
    ALLOWED_URI_REGEXP: /^(?:https?:|data:image\/)/i,
  });
}

// Rendu du document avec ses zones (variable/fixe/optionnel).
// `isBlockMovable` n'est requis qu'en mode editable (barre d'outils inline).
export function buildDocHtml(
  elements, structure, selectedBi, showExample,
  deletedPids = null, editable = false, isBlockMovable = null,
) {
  // Blocs imbriqués : un pid peut appartenir à un parent ET à un bloc
  // enfant (parent_label) ; on l'affecte au bloc le plus spécifique
  // (l'enfant), quel que soit l'ordre du tableau structure
  const idxToBlock = {};
  structure.forEach((b, bi) => {
    (b.paragraph_indices || []).forEach((pi) => {
      const prev = idxToBlock[pi];
      if (prev !== undefined && structure[prev].parent_label && !b.parent_label) return;
      idxToBlock[pi] = bi;
    });
  });
  const chipped = new Set();

  // Repeatable blocks show ONE example instance (unit_size paragraphs);
  // in placeholder mode, variable blocks keep only their first
  // paragraph as anchor. Everything else (incl. gaps) is skipped.
  const skip = new Set();
  structure.forEach((b, bi) => {
    if (b.type !== 'variable') return;
    const ids = b.paragraph_indices || [];
    if (!ids.length) return;
    const unit = Math.max(1, b.unit_size || 1);
    // Blocs enfants imbriqués dans ce bloc : les instances suivantes
    // d'un parent répétable escamotent aussi leurs zones enfants ; en
    // mode placeholder, chaque enfant garde son propre placeholder
    const childBis = new Set();
    if ((b.children || []).length) {
      structure.forEach((c, ci) => {
        if (c.parent_label && c.parent_label === b.label) childBis.add(ci);
      });
    }
    let drop;
    let anchor = -1;
    if (showExample) {
      drop = ids.slice(b.repeatable ? unit : ids.length);
    } else {
      // Ancre du placeholder : premier pid possédé en propre par le
      // bloc (pas par un enfant imbriqué qui ouvrirait sa propre zone)
      // et non supprimé (un pid de deletedPids est sauté au rendu, il
      // n'ouvrirait jamais la zone)
      const found = ids.find((pi) => idxToBlock[pi] === bi
        && !(deletedPids && deletedPids.has(pi)));
      anchor = found === undefined ? -1 : found;
      drop = ids.filter((pi) => pi !== anchor);
    }
    if (!drop.length) return;
    const dropChildren = showExample && childBis.size > 0;
    const lo = Math.min(...drop);
    const hi = Math.max(...drop);
    for (let i = lo; i <= hi; i++) {
      if (i === anchor) continue;
      const owner = idxToBlock[i];
      if (owner === bi || owner === undefined
          || (dropChildren && childBis.has(owner))) skip.add(i);
    }
  });

  const renderItems = (items) => {
    let html = '';
    let openBi = null;
    let hideZoneContent = false;
    const close = () => {
      if (openBi !== null) { html += '</div>'; openBi = null; hideZoneContent = false; }
    };

    for (const el of items) {
      if (el.kind === 'table') {
        close();
        html += '<table class="doc-tbl">';
        for (const row of el.rows || []) {
          html += '<tr>';
          for (const cell of row) {
            html += `<td>${renderItems(cell.items || [])}</td>`;
          }
          html += '</tr>';
        }
        html += '</table>';
        continue;
      }
      if (deletedPids && deletedPids.has(el.idx)) continue;
      if (skip.has(el.idx)) continue;
      const bi = idxToBlock[el.idx];
      if (bi === undefined) {
        close();
        const isEmpty = el.html.indexOf('>&nbsp;</p>') !== -1;
        if (isEmpty) { html += el.html; }
        else {
          html += `<div class="doc-unzoned" data-pidx="${el.idx}" title="Cliquer pour créer un bloc">${el.html}</div>`;
        }
        continue;
      }
      if (bi !== openBi) {
        close();
        const block = structure[bi];
        const isVariable = block.type === 'variable';
        const classes = ['doc-zone', isVariable ? 'zone-variable' : 'zone-fixed'];
        if (block.optional) classes.push('zone-optional');
        if (bi === selectedBi) classes.push('zone-selected');
        // Première ouverture de la zone : chip + placeholders. Une zone
        // parent peut se fragmenter autour de ses zones enfants, on
        // n'émet pas ces éléments sur les fragments suivants
        const firstOpen = !chipped.has(bi);
        let chip = '';
        if (firstOpen) {
          chipped.add(bi);
          const chipText = escapeHtml(block.label || '')
          + (block.parent_label ? ` · dans « ${escapeHtml(block.parent_label)} »` : '')
          + (block.optional ? ' · optionnel' : '')
          + (block.repeatable ? ' · répétable' : '');
          chip = `<span class="zone-chip ${isVariable ? 'chip-variable' : 'chip-fixed'}">${chipText}</span>`;
          // édition inline (éditeur de template uniquement) : barre
          // d'outils sur le bloc sélectionné, suppression rapide au
          // survol sinon (délégation onDocClick)
          if (editable && bi === selectedBi) {
            const t = [];
            t.push(`<button type="button" class="zt-btn zt-badge ${isVariable ? 'variable' : 'fixed'}" data-action="toggle-type" title="${isVariable ? 'Variable — cliquer pour fixer' : 'Fixe — cliquer pour rendre variable'}">${isVariable ? 'V' : 'F'}</button>`);
            t.push(`<button type="button" class="zt-btn${block.optional ? ' active' : ''}" data-action="toggle-optional">optionnel</button>`);
            t.push(`<button type="button" class="zt-btn${block.repeatable ? ' active' : ''}" data-action="toggle-repeatable">répétable</button>`);
            if (isBlockMovable && isBlockMovable(block)) {
              t.push('<button type="button" class="zt-btn" data-action="move-up" title="Monter ce bloc">↑</button>');
              t.push('<button type="button" class="zt-btn" data-action="move-down" title="Descendre ce bloc">↓</button>');
            }
            t.push('<button type="button" class="zt-btn zt-danger" data-action="delete" title="Supprimer ce bloc du template">✕</button>');
            chip += `<span class="zone-toolbar">${t.join('')}</span>`;
          } else if (editable) {
            chip += '<button type="button" class="zone-hover-del" data-action="delete" title="Supprimer ce bloc du template">✕</button>';
          }
        }
        html += `<div class="${classes.join(' ')}" data-bi="${bi}">${chip}`;
        openBi = bi;
        hideZoneContent = isVariable && !showExample;
        if (hideZoneContent) {
          if (firstOpen) {
            const rep = block.repeatable
              ? ' · répétable, autant d\'instances que nécessaire' : '';
            const desc = escapeHtml(block.instructions || '');
            html += `<p class="zone-placeholder">⟨ ${escapeHtml(block.label || 'Bloc')}${rep} ⟩${desc ? '<br>' + desc : ''}</p>`;
          }
        } else if (isVariable && block.repeatable && firstOpen) {
          html += '<p class="zone-placeholder">⟨ exemple d\'une instance · répétable, autant que nécessaire ⟩</p>';
        }
      }
      if (!hideZoneContent) html += el.html;
    }
    close();
    return html;
  };

  return DOMPurify.sanitize(renderItems(elements), {
    ALLOWED_URI_REGEXP: /^(?:https?:|data:image\/)/i,
  });
}
